import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Buildings, CheckCircle, IdentificationCard } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicCdd() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/cdd/${token}`)
      .then(r => {
        setData(r.data);
        const d = r.data.declaration || {};
        setF({
          nationality: d.nationality || "",
          country_of_birth: d.country_of_birth || "",
          residential_address: d.residential_address || r.data.person_address || "",
          contact_email: d.contact_email || r.data.person_email || "",
          contact_phone: d.contact_phone || r.data.person_phone || "",
          ownership_range: d.ownership_range || "",
          is_ultimate_bo: d.is_ultimate_bo || "",
          ultimate_bo_details: d.ultimate_bo_details || "",
          nominee_arrangement: !!d.nominee_arrangement,
          business_understanding: d.business_understanding || "",
          business_customers: d.business_customers || "",
          business_countries: d.business_countries || "",
          expected_transactions: d.expected_transactions || "",
          source_of_wealth: d.source_of_wealth || "",
          source_of_investment: d.source_of_investment || "",
          is_pep: !!d.is_pep,
          family_of_pep: !!d.family_of_pep,
          close_associate_of_pep: !!d.close_associate_of_pep,
          pep_details: d.pep_details || "",
          sanctions_listed: !!d.sanctions_listed,
          pending_investigation: !!d.pending_investigation,
          past_convictions: !!d.past_convictions,
          sanctions_details: d.sanctions_details || "",
          other_companies: d.other_companies || "",
          declaration_confirmed: !!d.declaration_confirmed,
          signature_name: d.signature_name || "",
          signature_date: d.signature_date || new Date().toISOString().slice(0,10),
        });
      })
      .catch(e => setError(e.response?.data?.detail || "Invalid or expired link"));
  }, [token]);

  const submit = async () => {
    if (!f.declaration_confirmed) return toast.error("You must confirm the declaration to submit");
    if (!f.signature_name.trim()) return toast.error("Please type your name as digital signature");
    setSaving(true);
    try {
      await axios.post(`${API}/public/cdd/${token}/submit`, f);
      toast.success("CDD declaration submitted");
      // reload to show submitted state
      const r = await axios.get(`${API}/public/cdd/${token}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  if (error) return <Shell><div className="p-10 text-center"><h1 className="text-2xl">Link invalid or expired</h1><p className="mt-2 text-muted-foreground">{error}</p></div></Shell>;
  if (!data || !f) return <Shell><div className="p-10 text-sm text-muted-foreground">Loading…</div></Shell>;

  const submitted = !!data.declaration?.submitted_at;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto p-6 md:p-12">
        <article className="border border-border bg-white">
          <header className="px-8 py-6 border-b border-border">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">CDD self-declaration · AMLATFPUAA 2001</div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Hi {data.person_name},</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Please complete this short Customer Due Diligence self-declaration for
              <b> {data.company_name}</b>{data.ssm_no ? <> · SSM {data.ssm_no}</> : null}.
              You are recorded as <b>{data.role}</b>.
              {submitted && <span className="ml-2 text-[#24A148] font-medium">Submitted ✓ — you may resubmit to update.</span>}
            </p>
          </header>

          <div className="px-8 py-6 space-y-6">
            <Sec title="1 · Personal identification">
              <Row><FieldR label="Full name (as per NRIC/Passport)" value={data.person_name} readonly /></Row>
              <Row><FieldR label="NRIC / Passport" value={data.person_ic || "—"} readonly /></Row>
              <Row2>
                <Field label="Nationality" v={f.nationality} onChange={v => setF({ ...f, nationality: v })} />
                <Field label="Country of birth" v={f.country_of_birth} onChange={v => setF({ ...f, country_of_birth: v })} />
              </Row2>
              <Row><Field label="Residential address" v={f.residential_address} onChange={v => setF({ ...f, residential_address: v })} multi /></Row>
              <Row2>
                <Field label="Contact email" v={f.contact_email} onChange={v => setF({ ...f, contact_email: v })} />
                <Field label="Contact phone" v={f.contact_phone} onChange={v => setF({ ...f, contact_phone: v })} />
              </Row2>
            </Sec>

            <Sec title="2 · Beneficial ownership">
              <Row><Field label="Your ownership percentage in this company" v={f.ownership_range} onChange={v => setF({ ...f, ownership_range: v })} placeholder="e.g. 25%, 50%, 100%" /></Row>
              <Row><Field label="Are you the ultimate beneficial owner (i.e. you personally own/control the shares, not on behalf of another party)?" v={f.is_ultimate_bo} onChange={v => setF({ ...f, is_ultimate_bo: v })} placeholder="Yes / No" /></Row>
              <Row><Field label="If not, please name the ultimate beneficial owner(s) behind you" v={f.ultimate_bo_details} onChange={v => setF({ ...f, ultimate_bo_details: v })} multi /></Row>
              <Check field="nominee_arrangement" label="I hold these shares under a nominee arrangement" f={f} setF={setF} />
            </Sec>

            <Sec title="3 · Understanding of the business">
              <Row><Field label="What does the company actually do?" v={f.business_understanding} onChange={v => setF({ ...f, business_understanding: v })} multi /></Row>
              <Row><Field label="Who are its main customers?" v={f.business_customers} onChange={v => setF({ ...f, business_customers: v })} multi /></Row>
              <Row><Field label="Where does it operate (countries / regions)?" v={f.business_countries} onChange={v => setF({ ...f, business_countries: v })} /></Row>
              <Row><Field label="Expected nature and volume of transactions" v={f.expected_transactions} onChange={v => setF({ ...f, expected_transactions: v })} multi /></Row>
            </Sec>

            <Sec title="4 · Source of wealth / funds">
              <Row><Field label="Your primary source of income / wealth" v={f.source_of_wealth} onChange={v => setF({ ...f, source_of_wealth: v })} multi
                          placeholder="e.g. salary from XYZ Sdn. Bhd., business income, investment returns" /></Row>
              <Row><Field label="How did you fund your investment in this company?" v={f.source_of_investment} onChange={v => setF({ ...f, source_of_investment: v })} multi /></Row>
            </Sec>

            <Sec title="5 · PEP self-declaration">
              <Check field="is_pep" label="I currently hold, or have previously held, a prominent public function (Politically Exposed Person)" f={f} setF={setF} />
              <Check field="family_of_pep" label="I am an immediate family member of a PEP" f={f} setF={setF} />
              <Check field="close_associate_of_pep" label="I am a close associate / known related party of a PEP" f={f} setF={setF} />
              <Row><Field label="If yes to any of the above, please provide details (position, country, dates)" v={f.pep_details} onChange={v => setF({ ...f, pep_details: v })} multi /></Row>
            </Sec>

            <Sec title="6 · Sanctions & integrity self-declaration">
              <Check field="sanctions_listed" label="I have ever been listed on any sanctions list (UN / OFAC / EU / MOHA / other)" f={f} setF={setF} />
              <Check field="pending_investigation" label="I am subject to any pending investigation for money-laundering, terrorism financing, fraud or corruption" f={f} setF={setF} />
              <Check field="past_convictions" label="I have any past convictions related to AML, fraud, tax evasion or financial crimes" f={f} setF={setF} />
              <Row><Field label="If yes to any of the above, please provide full details" v={f.sanctions_details} onChange={v => setF({ ...f, sanctions_details: v })} multi /></Row>
            </Sec>

            <Sec title="7 · Other business associations">
              <Row><Field label="Other companies where you are a director / shareholder / significant party" v={f.other_companies} onChange={v => setF({ ...f, other_companies: v })} multi
                          placeholder="Company name — SSM No. — Your role" /></Row>
            </Sec>

            <Sec title="8 · Declaration">
              <div className="border border-border bg-[#F9F9F9] p-4 text-sm space-y-3">
                <label className="flex items-start gap-2 cursor-pointer" data-testid="cdd-decl-confirmed">
                  <input type="checkbox" className="mt-1" checked={f.declaration_confirmed}
                         onChange={e => setF({ ...f, declaration_confirmed: e.target.checked })} />
                  <span>
                    I declare that the information provided in this form is true, accurate
                    and complete to the best of my knowledge. I understand that this
                    information will be used to fulfil the company secretary's Customer
                    Due Diligence obligations under the <b>AMLATFPUAA 2001</b> and the
                    Bank Negara Malaysia AML/CFT Guidelines for DNFBPs. I consent to the
                    processing and retention of this information for these purposes for
                    at least seven (7) years.
                  </span>
                </label>
                <Row2>
                  <Field label="Type your full name (digital signature)" v={f.signature_name} onChange={v => setF({ ...f, signature_name: v })} placeholder="Full name as per NRIC" />
                  <Field label="Date" v={f.signature_date} onChange={v => setF({ ...f, signature_date: v })} />
                </Row2>
              </div>
            </Sec>
          </div>

          <footer className="px-8 py-4 border-t border-border flex items-center justify-between">
            <div className="text-[11px] text-muted-foreground">
              Kept securely for CDD compliance. Retention: 7 years post-relationship.
            </div>
            <Button onClick={submit} disabled={saving}
                    className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]"
                    data-testid="cdd-submit-btn">
              {saving ? "Submitting…" : (submitted ? "Resubmit declaration" : "Submit declaration")}
            </Button>
          </footer>
        </article>
      </div>
    </Shell>
  );
}

function Sec({ title, children }) {
  return (
    <section>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground pb-1 mb-3 border-b border-border">{title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Row({ children }) { return <div>{children}</div>; }
function Row2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function FieldR({ label, value, readonly }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1 border border-border bg-[#F9F9F9] px-3 py-2 text-sm">{value}</div>
    </div>
  );
}
function Field({ label, v, onChange, multi, placeholder }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {multi ? (
        <Textarea rows={2} value={v} onChange={e => onChange(e.target.value)} className="mt-1 rounded-sm" placeholder={placeholder} />
      ) : (
        <Input value={v} onChange={e => onChange(e.target.value)} className="mt-1 rounded-sm" placeholder={placeholder} />
      )}
    </div>
  );
}
function Check({ field, label, f, setF }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`cdd-decl-${field}`}>
      <input type="checkbox" className="mt-1" checked={!!f[field]}
             onChange={e => setF({ ...f, [field]: e.target.checked })} />
      <span>{label}</span>
    </label>
  );
}
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center gap-3">
          <Buildings size={20} weight="duotone" />
          <span className="font-mono uppercase tracking-[0.2em] text-xs">Specfield Management Services Sdn. Bhd.</span>
          <span className="ml-auto text-[11px] font-mono text-muted-foreground flex items-center gap-1">
            <IdentificationCard size={14} weight="duotone" /> CDD portal
          </span>
        </div>
      </header>
      {children}
    </div>
  );
}
